import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RegistrationModal from '../components/RegistrationModal';
import styles from './Login.module.css';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const loggedInElsewhere = searchParams.get('reason') === 'logged_in_elsewhere';
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState(loggedInElsewhere ? 'You were logged in on another device. Please sign in again.' : '');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');

  useEffect(() => {
    if (searchParams.get('reason') === 'logged_in_elsewhere') {
      setInfoMessage('You were logged in on another device. Please sign in again.');
    }
  }, [searchParams]);

  useEffect(() => {
    // Check connection status
    const checkConnection = () => {
      if (!navigator.onLine) {
        setConnectionStatus('You are currently offline');
      } else {
        setConnectionStatus('');
      }
    };

    checkConnection();
    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', checkConnection);

    return () => {
      window.removeEventListener('online', checkConnection);
      window.removeEventListener('offline', checkConnection);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setIsSubmitting(true);
    try {
      const result = await login(formData.username, formData.password);
      if (result.success) {
        // Check if backend specified a redirect
        if (result.redirect) {
          navigate(result.redirect);
        } else {
          // Check if user has multiple roles (both school admin and teacher)
          // Redirect based on user type - School Admins go to School Admin Dashboard by default
          if (result.user && result.user.isAdmin) {
            navigate('/admin-dashboard');
          } else if (result.user && (result.user.isOrgAdmin || result.user.userType === 'organization')) {
            navigate('/org-admin-dashboard');
          } else if (result.user && result.user.isSchAdmin) {
            // School admin - always go to dashboard regardless of orgCode or subscription status
            navigate('/school-admin-dashboard');
          } else if (result.user && (result.user.userType === 'student' || result.user.role === 'student')) {
            // Students go to student dashboard
            navigate('/student-dashboard');
          } else {
            // All other regular users are teachers, so redirect to teacher dashboard
            navigate('/teacher-dashboard');
          }
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className={styles.loginContainer}>
        <div className={styles.loginFormContainer}>
          <div className={styles.loginRow}>
            <div className={styles.loginLogoCol}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img 
                  src="/img/kbhClassroomLogo.png" 
                  alt="Kids Black History logo" 
                  style={{ 
                    width: '526px', 
                    height: '450.59px',
                    display: 'block',
                    margin: '0 auto',
                    border: '4px solid var(--lightGreen)',
                    borderRadius: '16px',
                    boxSizing: 'border-box',
                    background: 'white',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
            <div className={styles.loginFormCol}>
              <div className={styles.loginFormWrapper}>
                {connectionStatus && <div className={styles.alert}>{connectionStatus}</div>}
                {infoMessage && <div className={styles.alert} style={{ backgroundColor: '#e7f3ff', color: '#004085', border: '1px solid #b8daff' }}>{infoMessage}</div>}
                {error && <div className={styles.alert}>{error}</div>}

                <h1 className={styles.loginHeading}>Login</h1>
                
                <form onSubmit={handleSubmit} className={styles.loginForm}>
                  <input
                    type="text"
                    name="username"
                    className={styles.formControl}
                    placeholder="Email Address"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />

                  <div className={styles.passwordContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className={`${styles.formControl} ${styles.passwordInput}`}
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={togglePasswordVisibility}
                    >
                      <i className={`fas fa-${showPassword ? 'eye' : 'eye-slash'}`}></i>
                      <span className={styles.srOnly}>{showPassword ? 'Hide' : 'Show'}</span>
                    </button>
                  </div>
                
                  <div className={styles.registerLink}>
                    <p>Don't have an account?&nbsp;
                      <a
                        href="#"
                        className={styles.linkButton}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowRegistrationModal(true);
                        }}
                      >
                        Register an account
                      </a>
                    </p>
                  </div>

                  <button type="submit" className={styles.loginBtn} disabled={isSubmitting}>
                    {isSubmitting ? 'Logging in...' : 'Login'}
                  </button>

                  <div className={styles.forgotPassword}>
                    <p>Forgot your password?&nbsp;
                      <a href="/request-password-reset">Reset Password</a>
                    </p>
                  </div>
                </form>

              </div>
            </div>
          </div>
        </div>
      </div>

      <RegistrationModal 
        isOpen={showRegistrationModal} 
        onClose={() => setShowRegistrationModal(false)} 
      />

    </>
  );
};

export default Login;
